import { normalizeShortFieldMessage } from "./messageNormalizer";
import { SubscriptionManager } from "./SubscriptionManager";
import { ConnectStatus, ConnectStatusListener, EventListener, SocketConfig } from "./types";
import { YNWebsocket } from "./websocket";

export const enum SOCKET_KEY {
    ORDERBOOK = "ORDERBOOK",
    CRYPTO = "CRYPTO",
}

export class ConnectManager {
    ws?: YNWebsocket;
    status: ConnectStatus = ConnectStatus.Disconnect;
    connectStatusListeners: ConnectStatusListener[] = [];
    eventListeners: EventListener[] = [];

    // 配置信息
    config: SocketConfig = {
        heartbeatInterval: 15 * 1000,
        reconnectInterval: 3000,
        pingMaxRetryCount: 3,
    };

    // 状态标记
    lockReconnect = false;
    needReconnect = true;
    pingRetryCount = 0;
    reConnectTimeout: any;
    heartTimer: any;

    // 已登录用户 ID（未登录时为 undefined）
    userId?: number;

    // 平台信息（h5 或 app）
    platform: "h5" | "app" = "h5";

    // 订阅管理器
    subscriptionManager?: SubscriptionManager<any>;

    private static instances: Map<string, ConnectManager> = new Map();
    private static defaultKey = SOCKET_KEY.ORDERBOOK; // 默认为orderbook 实例

    private constructor() {
        this.addConnectStatusListener( ( status ) => {
            if ( status === ConnectStatus.Connected && this.subscriptionManager ) {
                // 连接成功后，委托订阅管理器处理
                this.subscriptionManager.handleConnected( this.send.bind( this ) );
            }
        } );
    }

    /**
     * 获取默认实例（保持向后兼容）
     */
    public static shared() {
        return this.getInstance( this.defaultKey );
    }

    /**
     * 获取或创建指定的实例
     * @param key 实例标识符，通常使用 WebSocket URL 或自定义名称
     */
    public static getInstance( key: SOCKET_KEY ) {
        if ( !this.instances.has( key ) ) {
            this.instances.set( key, new ConnectManager() );
        }
        return this.instances.get( key )!;
    }

    /**
     * 移除指定实例
     */
    public static removeInstance( key: string ) {
        const instance = this.instances.get( key );
        if ( instance ) {
            instance.disconnect();
            this.instances.delete( key );
        }
    }

    /**
     * 移除所有实例
     */
    public static removeAllInstances() {
        this.instances.forEach( ( instance ) => instance.disconnect() );
        this.instances.clear();
    }

    /**
     * 初始化配置
     */
    init( config: SocketConfig ) {
        this.config = { ...this.config, ...config };
    }

    /**
     * 设置订阅管理器
     */
    setSubscriptionManager( manager: SubscriptionManager<any> ) {
        this.subscriptionManager = manager;
    }

    /**
     * 设置当前登录用户 ID，后续心跳包将携带此 ID
     * @param userId 用户 ID，传 undefined 表示用户已登出
     */
    setUserId( userId: number | undefined ) {
        this.userId = userId;
    }

    /**
     * 设置平台信息，后续心跳包将携带此信息
     * @param platform 平台标识，"h5" 或 "app"
     */
    setPlatform( platform: "h5" | "app" ) {
        this.platform = platform;
    }

    connect( url?: string ) {
        const finalUrl = url || this.config.url;
        if ( !finalUrl ) {
            console.error( "WebSocket URL is required" );
            return;
        }
        this.config.url = finalUrl;
        this.needReconnect = true;
        this.onlyConnect();
    }

    onlyConnect() {
        if ( this.status === ConnectStatus.Connected || this.status === ConnectStatus.Connecting ) {
            console.log( "Connecting or already connected, skipping." );
            return;
        }

        const addr = this.config.url;
        if ( !addr ) return;

        console.log( "Connecting to:", addr );
        this.status = ConnectStatus.Connecting;
        this.ws = new YNWebsocket( addr );

        this.ws.onopen( () => {
            console.log( "WebSocket opened" );
        } );

        this.ws.onmessage( ( data ) => {
            let parsedData: any;
            try {
                // 尝试解析 JSON
                parsedData = typeof data === "string" ? JSON.parse( data ) : data;
            } catch ( e ) {
                // 如果解析失败，说明是普通字符串，直接使用原始数据
                parsedData = {
                    type: "raw",
                    payload: data,
                };
            }
            this.onPacket( parsedData );
        } );

        this.ws.onclose( ( e ) => {
            console.log( "WebSocket closed", e );
            this.handleDisconnect();
        } );

        this.ws.onerror( ( e ) => {
            console.error( "WebSocket error", e );
            this.handleDisconnect();
        } );
    }

    private handleDisconnect() {
        if ( this.status !== ConnectStatus.Disconnect ) {
            this.status = ConnectStatus.Disconnect;
            this.notifyConnectStatusListeners();
        }
        if ( this.needReconnect ) {
            this.reConnect();
        }
    }

    disconnect() {
        this.needReconnect = false;
        console.log( "Disconnecting, auto-reconnect disabled" );
        this.onlyDisconnect();
    }

    onlyDisconnect() {
        this.stopHeart();
        this.stopReconnectTimer();
        if ( this.ws ) {
            this.ws.close();
            this.ws = undefined;
        }
        this.status = ConnectStatus.Disconnect;
    }

    reConnect() {
        if ( this.lockReconnect ) return;

        this.lockReconnect = true;
        this.stopReconnectTimer();

        console.log( `Reconnecting in ${this.config.reconnectInterval}ms...` );
        this.reConnectTimeout = setTimeout( () => {
            if ( this.ws ) {
                this.ws.close();
                this.ws = undefined;
            }
            this.onlyConnect();
            this.lockReconnect = false;
        }, this.config.reconnectInterval );
    }

    private stopHeart() {
        if ( this.heartTimer ) {
            clearInterval( this.heartTimer );
            this.heartTimer = null;
        }
    }

    private stopReconnectTimer() {
        if ( this.reConnectTimeout ) {
            clearTimeout( this.reConnectTimeout );
            this.reConnectTimeout = null;
        }
    }

    onPacket( data: any ) {
        if ( !data ) return;

        // v2 短字段消息展开（如 { t: "obs", p: {...} }）
        if ( !data.type ) {
            const normalized = normalizeShortFieldMessage( data );
            if ( normalized ) {
                this.notifyEventListeners( normalized.type, normalized.payload );
            }
            return;
        }

        switch ( data.type ) {
            case "connected":
                this.status = ConnectStatus.Connected;
                this.pingRetryCount = 0;
                this.notifyConnectStatusListeners();
                this.startHeartbeat();
                break;
            case "heartbeat":
            case "pong":
                this.pingRetryCount = 0;
                break;
            default:
                // 尝试由订阅管理器处理
                const handled = this.subscriptionManager?.handleMessage(
                    data,
                    this.send.bind( this ),
                    this.status === ConnectStatus.Connected,
                );

                // 如果不是订阅响应，则通知事件监听器
                if ( !handled ) {
                    this.notifyEventListeners( data.type, data.payload );
                }
                break;
        }
    }

    private startHeartbeat() {
        this.stopHeart();
        this.heartTimer = setInterval( () => {
            this.sendPing();
            if ( this.pingRetryCount > ( this.config.pingMaxRetryCount || 3 ) ) {
                console.log( "Ping timeout, reconnecting..." );
                this.onlyDisconnect();
                this.reConnect();
            }
        }, this.config.heartbeatInterval );
    }

    private sendPing() {
        this.pingRetryCount++;
        const payload: Record<string, any> = { type: "ping" };
        if ( this.userId ) {
            payload.uid = this.userId;
        }
        // 添加平台信息到 ping 消息
        payload.p = this.platform;
        this.send( JSON.stringify( payload ) );
    }

    send( data: any ) {
        this.ws?.send( data );
    }

    /**
     * 订阅事件
     * @param subscriptionId 订阅标识符
     */
    subscribeEvent<T = string>( subscriptionId: T ) {
        if ( !this.subscriptionManager ) {
            console.warn( "SubscriptionManager not set. Call setSubscriptionManager first." );
            return;
        }
        this.subscriptionManager.subscribe(
            subscriptionId,
            this.send.bind( this ),
            this.status === ConnectStatus.Connected,
        );
    }

    /**
     * 取消订阅
     * @param subscriptionId 订阅标识符
     */
    unsubscribeEvent<T = string>( subscriptionId: T ) {
        if ( !this.subscriptionManager ) {
            console.warn( "SubscriptionManager not set." );
            return;
        }
        this.subscriptionManager.unsubscribe( subscriptionId, this.send.bind( this ) );
    }

    // 事件监听管理
    addConnectStatusListener( listener: ConnectStatusListener ) {
        this.connectStatusListeners.push( listener );
    }

    removeConnectStatusListener( listener: ConnectStatusListener ) {
        this.connectStatusListeners = this.connectStatusListeners.filter( ( l ) => l !== listener );
    }

    notifyConnectStatusListeners( reasonCode?: number ) {
        this.connectStatusListeners.forEach( ( listener ) => listener( this.status, reasonCode ) );
    }

    addEventListeners( listener: EventListener ) {
        this.eventListeners.push( listener );
    }

    removeEventListeners( listener: EventListener ) {
        this.eventListeners = this.eventListeners.filter( ( l ) => l !== listener );
    }

    notifyEventListeners( type: string, payload?: any ) {
        this.eventListeners.forEach( ( listener ) => listener( type, payload ) );
    }
}
